import { useEffect, useRef, useState } from 'react'
import { ChevDownIcon } from '../../icons'

export type ExportMenuItem = {
	key: string
	label: React.ReactNode
	ariaLabel: string
	icon: React.ReactNode
	action: () => Promise<void> | void
	busy?: boolean
	disabled?: boolean
}

export function ExportSplitButton({
	items,
	buttonLabel = 'Export',
	width = 'w-72',
}: Readonly<{
	items: ExportMenuItem[]
	buttonLabel?: string
	width?: string
}>) {
	const [open, setOpen] = useState(false)
	const menuRef = useRef<HTMLDivElement>(null)
	const isAnyBusy = items.some((i) => i.busy)

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
				setOpen(false)
			}
		}
		if (open) document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [open])

	return (
		<div ref={menuRef} className='relative'>
			<button
				type='button'
				onClick={() => setOpen((v) => !v)}
				disabled={isAnyBusy}
				className='flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60'>
				{isAnyBusy ? 'Exporting…' : buttonLabel}
				<ChevDownIcon size={16} className='text-white' />
			</button>
			{open && (
				<div
					className={`absolute right-0 top-full z-20 mt-2 ${width} rounded-lg border border-neutral-200 bg-white shadow-lg overflow-hidden`}>
					{items.map((item) => (
						<button
							key={item.key}
							type='button'
							aria-label={item.ariaLabel}
							disabled={isAnyBusy || item.disabled}
							onClick={async () => {
								setOpen(false)
								await item.action()
							}}
							className='flex w-full items-center gap-3 px-4 py-3 text-left text-sm text-neutral-700 hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed'>
							{item.icon}
							<span>
								{item.label}
								{item.busy && <span className='ml-1 text-neutral-400'>— exporting…</span>}
							</span>
						</button>
					))}
				</div>
			)}
		</div>
	)
}

export function CsvIcon({ className }: Readonly<{ className?: string }>) {
	return (
		<svg
			width='20'
			height='20'
			viewBox='0 0 24 24'
			fill='none'
			stroke='currentColor'
			strokeWidth='1.6'
			strokeLinecap='round'
			strokeLinejoin='round'
			className={className}>
			<title>CSV file</title>
			<path d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z' />
			<polyline points='14 2 14 8 20 8' />
			<path d='M8 13h2' />
			<path d='M8 17h2' />
			<path d='M14 13h2' />
			<path d='M14 17h2' />
		</svg>
	)
}
